import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getFAQs, deleteFAQ } from '../../actions/faq';
import { 
  List, 
  Plus, 
  Trash2, 
  Edit, 
  Eye, 
  Search,
  HelpCircle,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Tag
} from 'lucide-react';

const FAQs = ({ 
  getFAQs, 
  deleteFAQ, 
  faq: { faqs, loading }
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredFAQs, setFilteredFAQs] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    document.title = 'FAQs';
    getFAQs();
  }, [getFAQs]);

  useEffect(() => {
    if (faqs) {
      setFilteredFAQs(
        faqs.filter(item => 
          item.category?.some(cat => 
            cat.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            cat.faqs?.some(faq => 
              faq.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              faq.answer?.toLowerCase().includes(searchTerm.toLowerCase())
            )
          )
        )
      );
    }
  }, [faqs, searchTerm]);

  const handleSearch = e => {
    setSearchTerm(e.target.value);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (itemToDelete) {
      deleteFAQ(itemToDelete._id);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getStatusBadge = (item) => {
    if (item.active) {
      return <span className="badge bg-success">Active</span>;
    } else {
      return <span className="badge bg-secondary">Inactive</span>;
    }
  };

  const getTotalFAQs = (item) => {
    if (!item.category) return 0;
    return item.category.reduce((total, cat) => total + (cat.faqs?.length || 0), 0);
  };

  return (
    <Fragment>
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-0 text-gray-800 d-flex align-items-center">
              <HelpCircle size={28} className="me-3 text-primary" />
              FAQs
            </h1>
            <p className="text-muted">
              Manage frequently asked questions and help content
            </p>
          </div>
          <Link to="/inventory/v1.2/faqs/new" className="btn btn-primary d-flex align-items-center">
            <Plus size={18} className="me-2" />
            Add New FAQ
          </Link>
        </div>

        {/* Search Bar */}
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <div className="input-group">
              <span className="input-group-text bg-light">
                <Search size={18} className="text-muted" />
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder="Search by category, question, or answer..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="row">
            {filteredFAQs.length > 0 ? (
              filteredFAQs.map(item => (
                <div key={item._id} className="col-lg-6 col-xl-4 mb-4">
                  <div className="card shadow-sm h-100">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                      <h5 className="mb-0 d-flex align-items-center">
                        <HelpCircle size={20} className="me-2 text-primary" />
                        FAQ Set
                      </h5>
                      {getStatusBadge(item)}
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <p className="text-muted mb-1">Categories:</p>
                        <div className="d-flex flex-wrap gap-1">
                          {item.category?.map((cat, index) => (
                            <span key={index} className="badge bg-light text-dark border">
                              <Tag size={12} className="me-1" />
                              {cat.name}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="mb-3">
                        <p className="text-muted mb-1">Total FAQs:</p>
                        <p className="fw-bold mb-0">{getTotalFAQs(item)} questions</p>
                      </div>
                      <div className="mb-3">
                        <p className="text-muted mb-1">Created:</p>
                        <p className="fw-bold mb-0 d-flex align-items-center">
                          <Clock size={14} className="me-1" />
                          {formatDate(item.createdAt)}
                        </p>
                      </div>
                      <div className="mb-3">
                        <p className="text-muted mb-1">Updated:</p>
                        <p className="fw-bold mb-0 d-flex align-items-center">
                          <Clock size={14} className="me-1" />
                          {formatDate(item.updatedAt)}
                        </p>
                      </div>
                      <div className="d-flex align-items-center mb-3">
                        <span className="me-2">Status:</span>
                        {item.active ? (
                          <span className="d-flex align-items-center text-success">
                            <CheckCircle size={16} className="me-1" />
                            Active
                          </span>
                        ) : (
                          <span className="d-flex align-items-center text-secondary">
                            <XCircle size={16} className="me-1" />
                            Inactive
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="card-footer bg-white d-flex justify-content-between">
                      <Link
                        to={`/inventory/v1.2/faqs/${item._id}`}
                        className="btn btn-sm btn-outline-primary d-flex align-items-center"
                      >
                        <Eye size={14} className="me-1" />
                        View
                      </Link>
                      <button
                        onClick={() => handleDeleteClick(item)}
                        className="btn btn-sm btn-outline-danger d-flex align-items-center"
                      >
                        <Trash2 size={14} className="me-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12">
                <div className="alert alert-info">
                  No FAQs found. {searchTerm && 'Try adjusting your search.'}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header border-0">
                  <h5 className="modal-title d-flex align-items-center text-danger">
                    <AlertTriangle size={24} className="me-2" />
                    Confirm Delete
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={cancelDelete}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="text-center">
                    <div className="mb-3">
                      <Trash2 size={48} className="text-danger" />
                    </div>
                    <h6 className="mb-3">Delete FAQ Set?</h6>
                    <p className="text-muted mb-4">
                      This action cannot be undone. The FAQ set will be permanently deleted.
                    </p>
                    <div className="alert alert-warning d-flex align-items-center">
                      <AlertTriangle size={20} className="me-2" />
                      <small>
                        <strong>Warning:</strong> This will permanently delete this FAQ set and all its questions.
                      </small>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={cancelDelete}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={confirmDelete}
                  >
                    <Trash2 size={16} className="me-2" />
                    Delete FAQ Set
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
};

FAQs.propTypes = {
  getFAQs: PropTypes.func.isRequired,
  deleteFAQ: PropTypes.func.isRequired,
  faq: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  faq: state.faq
});

export default connect(
  mapStateToProps,
  { getFAQs, deleteFAQ }
)(FAQs);
